import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, photoUrl, errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ListingCard } from "../components/ListingCard";
import { StarRating } from "../components/StarRating";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { RoleAvatar, StaffTag } from "../components/RoleAvatar";
import { ReportUserDialog } from "../components/ReportUserDialog";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { BadgeCheck, Calendar, Package, Star, MessageSquarePlus, Flag, ShieldAlert, ShieldCheck, Globe, Clock } from "lucide-react";
import VerifiedProfile from "../components/profile/VerifiedProfile";
import { MemberLitters } from "../components/profile/MemberLitters";
import { ReviewText } from "../components/profile/ReviewText";
import { ProfileText } from "../components/ProfileText";

function timeAgoShort(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "moments ago";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

export default function SellerProfile() {
  const { id } = useParams();
  const { user, setAuthOpen } = useAuth();
  const [data, setData] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get(`/users/${id}`).then((r) => setData(r.data));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!data) return <div className="max-w-5xl mx-auto px-4 py-10"><Skeleton className="h-40 rounded-2xl" /></div>;

  const submitReview = async () => {
    if (!user) return setAuthOpen(true);
    if (!rating) return toast.error("Pick a star rating");
    try {
      await api.post(`/users/${id}/reviews`, { rating, comment });
      toast.success("Review submitted!");
      setShowForm(false); setRating(0); setComment("");
      load();
    } catch (e) { toast.error(errText(e)); }
  };

  const year = data.created_at ? new Date(data.created_at).getFullYear() : "—";
  const mine = user && user.user_id === id;
  const isStaff = user && (user.role === "admin" || user.role === "moderator");
  const isVerified = data.verified_seller || data.verified_business || data.is_verified_seller || data.verified_breeder;

  if (isVerified) {
    return (
      <div>
        <VerifiedProfile data={data} mine={mine} onReload={load} />
        {isStaff && (
          <div className="max-w-5xl mx-auto px-4 sm:px-8 pb-10 -mt-6">
            <StaffTrustScore userId={id} />
            <StaffIpHistory userId={id} />
            <StaffReportHistory userId={id} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <div className="bg-white rounded-2xl border border-slate-200/80 p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <RoleAvatar user={{ role: data.role, picture: data.picture, name: data.name }} size={80} className="text-2xl" />
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-2xl font-extrabold text-[#12234A]" data-testid="seller-name">{data.name}</h1>
              <StaffTag role={data.role} />
              {data.verified_business && <span className="flex items-center gap-1 bg-amber-500 text-white text-xs font-semibold px-2 py-1 rounded-full" data-testid="verified-badge-profile-business"><BadgeCheck size={14} className="text-white" /> Verified Business</span>}
              {(data.verified_seller || (!data.verified_business && data.is_verified_seller)) && <span className="flex items-center gap-1 bg-emerald-600 text-white text-xs font-semibold px-2 py-1 rounded-full" data-testid="verified-badge-profile"><BadgeCheck size={14} className="text-white" /> Verified Seller</span>}
              {data.presence === "active" && <span data-testid="profile-presence" className="flex items-center gap-1.5 bg-emerald-50 text-emerald-700 text-xs font-semibold px-2 py-1 rounded-full"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> Active now</span>}
              {data.presence === "away" && <span data-testid="profile-presence" className="flex items-center gap-1.5 bg-amber-50 text-amber-700 text-xs font-semibold px-2 py-1 rounded-full"><span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Away</span>}
              {data.presence === "offline" && data.last_seen && <span data-testid="profile-presence" className="flex items-center gap-1 text-slate-400 text-xs font-medium px-2 py-1" title={`Last active ${new Date(data.last_seen).toLocaleString()}`}><Clock size={12} /> Active {timeAgoShort(data.last_seen)}</span>}
            </div>
            <p className="text-slate-500 mt-1">{data.username ? `@${data.username} · ` : ""}{data.seller_type} · Member since {year}</p>
            {data.bio && <div className="mt-3 max-w-2xl"><ProfileText userId={data.user_id} field="bio" text={data.bio} className="text-slate-600" testid="profile-bio" /></div>}
            <div className="flex flex-wrap gap-6 mt-4">
              <Stat icon={Star} label="Rating" value={data.review_count ? `${data.avg_rating} (${data.review_count})` : "No reviews"} />
              <Stat icon={Package} label="Active listings" value={data.active_listings} />
            </div>
          </div>
          {!mine && (
            <div className="flex flex-col gap-2 sm:items-end">
              <Button onClick={() => (user ? setShowForm(!showForm) : setAuthOpen(true))} data-testid="write-review-btn" variant="outline" className="rounded-xl gap-2"><MessageSquarePlus size={16} /> Write a review</Button>
              {data.role === "user" && (
                <ReportUserDialog userId={id} userName={data.name} trigger={
                  <button data-testid="report-seller-btn" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-amber-600 transition-colors cursor-pointer"><Flag size={14} /> Report this user</button>
                } />
              )}
            </div>
          )}
        </div>

        {showForm && (
          <div className="mt-6 bg-slate-50 rounded-xl p-4">
            <StarRating value={rating} onChange={setRating} size={26} />
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} data-testid="review-comment" className="mt-3 rounded-xl bg-white" rows={3} placeholder="Share your experience with this seller…" />
            <Button onClick={submitReview} data-testid="submit-review-btn" className="mt-3 rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white">Submit review</Button>
          </div>
        )}
      </div>

      {isStaff && <StaffTrustScore userId={id} />}
      {isStaff && <StaffIpHistory userId={id} />}
      {isStaff && <StaffReportHistory userId={id} />}

      {data.hosts_litters && <MemberLitters userId={id} mine={mine} />}

      {data.reviews?.length > 0 && (
        <div className="mt-8">
          <h2 className="font-display text-xl font-bold text-[#12234A] mb-4">Reviews ({data.review_count})</h2>
          <div className="space-y-3">
            {data.reviews.map((r) => (
              <div key={r.id} className="bg-white rounded-2xl border border-slate-200/80 p-5" data-testid={`review-${r.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-9 w-9"><AvatarImage src={photoUrl(r.reviewer_picture)} /><AvatarFallback className="bg-[#12234A] text-white text-xs">{r.reviewer_name?.[0]}</AvatarFallback></Avatar>
                    <p className="font-semibold text-[#12234A] text-sm">{r.reviewer_name}</p>
                  </div>
                  <StarRating value={r.rating} size={14} />
                </div>
                <ReviewText r={r} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8">
        <h2 className="font-display text-xl font-bold text-[#12234A] mb-4">Listings by {data.name}</h2>
        {data.listings?.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">{data.listings.map((l) => <ListingCard key={l.id} listing={{ ...l, seller: { is_verified_seller: data.is_verified_seller, verified_seller: data.verified_seller, verified_business: data.verified_business, role: data.role } }} />)}</div>
        ) : <p className="text-slate-500">No active listings.</p>}
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2"><Icon size={18} className="text-[#F5551E]" /><div><p className="text-xs text-slate-400">{label}</p><p className="font-semibold text-[#12234A] text-sm">{value}</p></div></div>
);

const TYPE_LABEL = { listing: "Ad report", message: "Message report", user: "Profile report" };

const TRUST_COLORS = {
  emerald: { bar: "bg-emerald-500", text: "text-emerald-600", ring: "border-emerald-200", chip: "bg-emerald-100 text-emerald-700" },
  green: { bar: "bg-green-500", text: "text-green-600", ring: "border-green-200", chip: "bg-green-100 text-green-700" },
  amber: { bar: "bg-amber-500", text: "text-amber-600", ring: "border-amber-200", chip: "bg-amber-100 text-amber-700" },
  orange: { bar: "bg-orange-500", text: "text-orange-600", ring: "border-orange-200", chip: "bg-orange-100 text-orange-700" },
  red: { bar: "bg-red-500", text: "text-red-600", ring: "border-red-200", chip: "bg-red-100 text-red-700" },
};

function StaffTrustScore({ userId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/admin/users/${userId}/trust`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [userId]);
  if (!data) return null;
  const c = TRUST_COLORS[data.color] || TRUST_COLORS.amber;
  return (
    <div className={`mt-6 bg-white rounded-2xl border ${c.ring} p-5`} data-testid="trust-score">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck size={18} className={c.text} />
        <h2 className="font-display text-lg font-bold text-[#12234A]">Trust score</h2>
        <span className={`ml-auto text-xs font-bold px-2.5 py-1 rounded-full ${c.chip}`} data-testid="trust-label">{data.label}</span>
      </div>
      <p className="text-xs text-slate-400 mb-3">Staff-only. A quick read on this seller from verification, reviews &amp; reports.</p>
      <div className="flex items-center gap-4">
        <div className={`text-4xl font-extrabold ${c.text}`} data-testid="trust-value">{data.score}<span className="text-lg text-slate-300 font-bold">/100</span></div>
        <div className="flex-1">
          <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full rounded-full ${c.bar}`} style={{ width: `${data.score}%` }} /></div>
          <div className="flex gap-4 mt-2 text-xs text-slate-500">
            <span>{data.verified ? "✓ Verified" : "Not verified"}</span>
            <span>{data.reviews.count ? `${data.reviews.avg}★ (${data.reviews.count})` : "No reviews"}</span>
            <span className={data.reports.open ? "text-red-600 font-semibold" : ""}>{data.reports.total ? `${data.reports.open} open / ${data.reports.total} reports` : "No reports"}</span>
          </div>
        </div>
      </div>
      <div className="mt-4 border-t border-slate-100 pt-3 space-y-1.5">
        {data.factors.map((f, i) => (
          <div key={i} className="flex items-center justify-between text-xs" data-testid={`trust-factor-${i}`}>
            <span className="text-slate-600"><strong className="text-[#12234A]">{f.label}</strong> · {f.detail}</span>
            <span className={`font-bold shrink-0 ml-3 ${typeof f.impact === "number" ? (f.impact > 0 ? "text-emerald-600" : f.impact < 0 ? "text-red-600" : "text-slate-400") : "text-red-600"}`}>
              {typeof f.impact === "number" ? (f.impact > 0 ? `+${f.impact}` : f.impact) : f.impact}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StaffIpHistory({ userId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/staff/users/${userId}/ip-history`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [userId]);
  if (!data) return null;
  const fmt = (iso) => iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";
  return (
    <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-5" data-testid="ip-history">
      <div className="flex items-center gap-2">
        <Globe size={18} className="text-[#12234A]" />
        <h3 className="font-display font-bold text-[#12234A]">IP history</h3>
        <span className="ml-auto text-xs text-slate-400">{data.count} events · {data.unique_ip_count} unique IPs</span>
      </div>

      {data.shared_ip_warning && (
        <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-3" data-testid="shared-ip-warning">
          <p className="text-sm font-bold text-amber-800 flex items-center gap-1.5"><ShieldAlert size={15} /> Shared IP detected — possible duplicate accounts</p>
          <div className="mt-2 space-y-1.5">
            {data.shared_ips.map((s) => (
              <div key={s.ip} className="text-xs text-amber-900" data-testid={`shared-ip-${s.ip}`}>
                <span className="font-mono font-semibold">{s.ip}</span>
                {s.geo?.label && <span className="text-amber-700"> · {s.geo.label}</span>}
                <span className="text-amber-700"> · also used by {s.other_count} other account{s.other_count === 1 ? "" : "s"}:</span>
                <span className="ml-1">
                  {s.accounts.map((a, i) => (
                    <span key={a.user_id}>{i > 0 && ", "}<Link to={`/seller/${a.user_id}`} className="font-semibold underline hover:text-amber-950">{a.name}</Link></span>
                  ))}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between text-sm bg-slate-50 rounded-xl px-3 py-2">
        <span className="text-slate-500">Signup IP</span>
        <span className="font-mono font-semibold text-[#12234A]" data-testid="ip-signup">{data.signup_ip || "—"} <span className="text-xs text-slate-400 font-sans">· {data.signup_geo?.label || "location unknown"} · {fmt(data.signup_at)}</span></span>
      </div>
      {data.logs.length === 0 ? (
        <p className="text-sm text-slate-400 mt-3">No IP events recorded yet.</p>
      ) : (
        <div className="mt-3 space-y-1.5 max-h-64 overflow-y-auto">
          {data.logs.map((l) => (
            <div key={l.id} className="flex items-center gap-3 text-xs border-b border-slate-50 pb-1.5" data-testid={`ip-log-${l.id}`}>
              <span className="font-mono font-semibold text-[#12234A] w-32 shrink-0">{l.ip || "—"}{l.shared_count > 0 && <span title="Shared with other accounts" className="ml-1 text-amber-600">⚠</span>}</span>
              <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 shrink-0">{l.event}</span>
              <span className="text-slate-500 shrink-0 w-40 truncate">{l.geo?.label || "—"}</span>
              <span className="text-slate-400 truncate flex-1" title={l.user_agent}>{l.user_agent || ""}</span>
              <span className="text-slate-500 whitespace-nowrap shrink-0">{fmt(l.at)}</span>
            </div>
          ))}
        </div>
      )}
      <p className="text-[11px] text-slate-400 mt-3">Collected for security &amp; fraud prevention. Location is approximate. Staff-only.</p>
    </div>
  );
}

function StaffReportHistory({ userId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/admin/users/${userId}/reports`).then((r) => setData(r.data)).catch(() => setData({ reports: [], total: 0, open_count: 0 }));
  }, [userId]);
  if (!data) return <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-5"><Skeleton className="h-20 rounded-xl" /></div>;
  return (
    <div className="mt-6 bg-white rounded-2xl border border-amber-200 p-5" data-testid="report-history">
      <div className="flex items-center gap-2 mb-1">
        <ShieldAlert size={18} className="text-amber-600" />
        <h2 className="font-display text-lg font-bold text-[#12234A]">Report history</h2>
        <span className="ml-auto text-xs font-semibold text-slate-500">{data.total} total · {data.open_count} open</span>
      </div>
      <p className="text-xs text-slate-400 mb-3">Staff-only. Every ad, message &amp; profile report filed against this user.</p>
      {data.reports.length === 0 ? (
        <p className="text-sm text-slate-500">No reports on record. 🎉</p>
      ) : (
        <div className="space-y-2">
          {data.reports.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2" data-testid={`report-history-item-${r.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#12234A] text-white">{TYPE_LABEL[r.type] || r.type}</span>
                <span className="text-xs font-semibold text-red-600">{r.reason}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${r.status === "open" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{r.status === "open" ? "open" : `${r.resolution || "resolved"}`}</span>
                <span className="ml-auto text-[10px] text-slate-400">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
              {r.type === "listing" && r.listing_id && <Link to={`/listing/${r.listing_id}`} className="text-xs text-[#12234A] hover:text-[#F5551E] mt-1 inline-block">{r.listing_title || "View ad"}</Link>}
              {r.type === "message" && r.message_body && <p className="text-xs text-slate-600 mt-1 italic">"{r.message_body}"</p>}
              {r.details && <p className="text-xs text-slate-500 mt-1">Note: "{r.details}"</p>}
              <p className="text-[10px] text-slate-400 mt-1">by {r.reporter_name}{r.resolved_by && ` · handled by ${r.resolved_by}`}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
